import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Mail, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Email Config Form Component
function EmailConfigForm({ config, index, onUpdate, onRemove }) {
  const [testing, setTesting] = useState({ smtp: false, imap: false });
  const [testResults, setTestResults] = useState({ smtp: null, imap: null });

  const testSMTP = async () => {
    setTesting({ ...testing, smtp: true });
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/actors/test/email-configs/test-smtp`,
        config.smtp,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResults({ ...testResults, smtp: response.data });
      toast.success(response.data.message || "SMTP test successful!");
    } catch (error) {
      const message = error.response?.data?.message || "SMTP test failed";
      setTestResults({ ...testResults, smtp: { success: false, message } });
      toast.error(message);
    } finally {
      setTesting({ ...testing, smtp: false });
    }
  };

  const testIMAP = async () => {
    setTesting({ ...testing, imap: true });
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/actors/test/email-configs/test-imap`,
        config.imap,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResults({ ...testResults, imap: response.data });
      toast.success(response.data.message || "IMAP test successful!");
    } catch (error) {
      const message = error.response?.data?.message || "IMAP test failed";
      setTestResults({ ...testResults, imap: { success: false, message } });
      toast.error(message);
    } finally {
      setTesting({ ...testing, imap: false });
    }
  };

  return (
    <Card className="border-2">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Email Account {index + 1}</CardTitle>
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Email Address */}
        <div>
          <Label>Email Address</Label>
          <Input
            type="email"
            placeholder="actor@example.com"
            value={config.email_address}
            onChange={(e) => onUpdate({ ...config, email_address: e.target.value })}
          />
        </div>

        {/* SMTP Configuration */}
        <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">SMTP (Outgoing Mail)</Label>
            <Button type="button" size="sm" onClick={testSMTP} disabled={testing.smtp}>
              {testing.smtp ? "Testing..." : "Test SMTP"}
            </Button>
          </div>
          {testResults.smtp && (
            <div className={`text-sm p-2 rounded ${testResults.smtp.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {testResults.smtp.message}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Host</Label>
              <Input
                placeholder="smtp.gmail.com"
                value={config.smtp.host}
                onChange={(e) => onUpdate({ ...config, smtp: { ...config.smtp, host: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Port</Label>
              <Input
                type="number"
                placeholder="587"
                value={config.smtp.port}
                onChange={(e) => onUpdate({ ...config, smtp: { ...config.smtp, port: parseInt(e.target.value) } })}
              />
            </div>
            <div>
              <Label className="text-sm">Username</Label>
              <Input
                placeholder="username"
                value={config.smtp.username}
                onChange={(e) => onUpdate({ ...config, smtp: { ...config.smtp, username: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={config.smtp.password}
                onChange={(e) => onUpdate({ ...config, smtp: { ...config.smtp, password: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Security</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={config.smtp.security}
                onChange={(e) => onUpdate({ ...config, smtp: { ...config.smtp, security: e.target.value } })}
              >
                <option value="TLS">TLS</option>
                <option value="SSL">SSL</option>
                <option value="None">None</option>
              </select>
            </div>
          </div>
        </div>

        {/* IMAP Configuration */}
        <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">IMAP (Incoming Mail)</Label>
            <Button type="button" size="sm" onClick={testIMAP} disabled={testing.imap}>
              {testing.imap ? "Testing..." : "Test IMAP"}
            </Button>
          </div>
          {testResults.imap && (
            <div className={`text-sm p-2 rounded ${testResults.imap.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {testResults.imap.message}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Host</Label>
              <Input
                placeholder="imap.gmail.com"
                value={config.imap.host}
                onChange={(e) => onUpdate({ ...config, imap: { ...config.imap, host: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Port</Label>
              <Input
                type="number"
                placeholder="993"
                value={config.imap.port}
                onChange={(e) => onUpdate({ ...config, imap: { ...config.imap, port: parseInt(e.target.value) } })}
              />
            </div>
            <div>
              <Label className="text-sm">Username</Label>
              <Input
                placeholder="username"
                value={config.imap.username}
                onChange={(e) => onUpdate({ ...config, imap: { ...config.imap, username: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={config.imap.password}
                onChange={(e) => onUpdate({ ...config, imap: { ...config.imap, password: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Security</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={config.imap.security}
                onChange={(e) => onUpdate({ ...config, imap: { ...config.imap, security: e.target.value } })}
              >
                <option value="SSL">SSL</option>
                <option value="TLS">TLS</option>
                <option value="None">None</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sync Configuration */}
        <div className="space-y-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <Label className="text-base font-semibold">Sync Settings</Label>
          
          <div>
            <Label className="text-sm">Sync Interval</Label>
            <select
              className="w-full border rounded px-3 py-2"
              value={config.sync_interval || 60}
              onChange={(e) => onUpdate({ ...config, sync_interval: parseInt(e.target.value) })}
            >
              <option value={60}>Every 1 hour</option>
              <option value={120}>Every 2 hours</option>
              <option value={180}>Every 3 hours</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              How often should we check for new emails? Longer intervals improve performance.
            </p>
          </div>

          <div>
            <Label className="text-sm">Number of Emails to Fetch</Label>
            <select
              className="w-full border rounded px-3 py-2"
              value={config.fetch_limit || 200}
              onChange={(e) => onUpdate({ ...config, fetch_limit: parseInt(e.target.value) })}
            >
              <option value={100}>Fetch 100 emails</option>
              <option value={200}>Fetch 200 emails</option>
              <option value={400}>Fetch 400 emails</option>
              <option value={800}>Fetch 800 emails</option>
              <option value={1000}>Fetch 1000 emails</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              How many of the latest emails should be synced from your inbox?
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


export default function AIActorEmailConfig() {
  const { actorId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email_configs: []
  });

  useEffect(() => {
    fetchActorData();
  }, [actorId]);

  const fetchActorData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const actor = response.data;
      setFormData({
        email_configs: actor.email_configs || []
      });
    } catch (error) {
      toast.error("Failed to fetch actor data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      
      // CRITICAL: Fetch current actor data to avoid overwriting other fields
      const currentResponse = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const currentActor = currentResponse.data;
      
      // Merge current actor data with our email_configs changes
      const mergedData = {
        ...currentActor,                      // Keep all existing fields
        email_configs: formData.email_configs // Update only email_configs
      };
      
      // Save merged data
      await axios.put(`${API}/ai/actors/${actorId}`, mergedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Email configuration updated successfully");
    } catch (error) {
      toast.error("Failed to update email configuration");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading email configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/ai/actors/manage/${actorId}`)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Manage
        </Button>
        <h1 className="text-2xl font-bold">Email Configuration</h1>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Email Configuration Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Configuration
          </CardTitle>
          <CardDescription>
            Configure SMTP and IMAP settings for this AI Actor to send and receive emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
                {formData.email_configs && formData.email_configs.map((config, index) => (
                  <EmailConfigForm
                    key={config.id || index}
                    config={config}
                    index={index}
                    onUpdate={(updatedConfig) => {
                      const newConfigs = [...formData.email_configs];
                      newConfigs[index] = updatedConfig;
                      setFormData({ ...formData, email_configs: newConfigs });
                    }}
                    onRemove={() => {
                      const newConfigs = formData.email_configs.filter((_, i) => i !== index);
                      setFormData({ ...formData, email_configs: newConfigs });
                    }}
                  />
                ))}
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const newConfig = {
                      id: Date.now().toString(),
                      email_address: '',
                      smtp: { host: '', port: 587, username: '', password: '', security: 'TLS' },
                      imap: { host: '', port: 993, username: '', password: '', security: 'SSL' },
                      is_active: true
                    };
                    setFormData({ ...formData, email_configs: [...(formData.email_configs || []), newConfig] });
                  }}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Email Account
                </Button>
        </CardContent>
      </Card>
    </div>
  );
}
